// src/pages/DirectoryPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMemberDirectory } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY = '#1B3A6B';
const GOLD = '#C9A84C';

export default function DirectoryPage() {
  const navigate              = useNavigate();

  const [members,  setMembers]  = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  // Filters
  const [search,    setSearch]    = useState('');
  const [houseFilter, setHouseFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  const HOUSES = [
    'Aggrey','Cadbury','Clark','Fraser','Guggisberg',
    'Gyamfi','House 17','Kingsley','Livingstone',
    'Lugard','Macarthy','Slessor','SOA','Stopford'
  ];

  useEffect(() => {
    async function load() {
      try {
        const result = await getMemberDirectory();
        setMembers(result.members || []);
        setFiltered(result.members || []);
      } catch (err) {
        setError('Could not load directory: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Apply filters whenever search/filter changes
  useEffect(() => {
    let list = [...members];

    // Search — matches name, nickname, marital name, occupation
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.name?.toLowerCase().includes(q) ||
        m.nickname?.toLowerCase().includes(q) ||
        m.maritalName?.toLowerCase().includes(q) ||
        m.occupation?.toLowerCase().includes(q) ||
        m.location?.toLowerCase().includes(q)
      );
    }

    // House filter
    if (houseFilter) {
      list = list.filter(m => m.boardingHouse === houseFilter);
    }

    // Country filter
    if (countryFilter) {
      list = list.filter(m => {
        const country = m.location?.split(',').pop()?.trim() || '';
        return country.toLowerCase() === countryFilter.toLowerCase();
      });
    }

    setFiltered(list);
  }, [search, houseFilter, countryFilter, members]);

  // Get unique countries from members
  const countries = [...new Set(
    members
      .map(m => m.location?.split(',').pop()?.trim())
      .filter(Boolean)
  )].sort();


  const clearFilters = () => {
    setSearch('');
    setHouseFilter('');
    setCountryFilter('');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getRoleColor = (r) => {
    const map = {
      executive: { bg: '#fdecea', color: '#c0392b' },
      treasurer: { bg: '#fff3cd', color: '#856404' },
      house_rep: { bg: '#d4edda', color: '#1a6b3a' },
      member:    { bg: '#d6eaf8', color: '#1B3A6B' }
    };
    return map[r] || map.member;
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      {/* Content */}
      <div style={{
        maxWidth: '1000px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff'
        }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
            👥 Member Directory
          </h2>
          <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
            {loading
              ? 'Loading...'
              : `${filtered.length} of ${members.length} members`
            }
          </p>
        </div>

        {/* Search and Filters */}
        <div style={{
          background: '#fff', borderRadius: '12px',
          padding: '16px 20px', marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          {/* Search bar */}
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍  Search by name, nickname, occupation, location..."
            style={{
              width: '100%', padding: '11px 14px',
              border: '2px solid #e0e0e0', borderRadius: '8px',
              fontSize: '14px', outline: 'none',
              boxSizing: 'border-box', marginBottom: '12px'
            }}
          />

          {/* Filter row */}
          <div style={{
            display: 'flex', gap: '12px',
            flexWrap: 'wrap', alignItems: 'center'
          }}>
            {/* House filter */}
            <select
              value={houseFilter}
              onChange={e => setHouseFilter(e.target.value)}
              style={{
                padding: '8px 12px', border: '2px solid #e0e0e0',
                borderRadius: '8px', fontSize: '14px',
                outline: 'none', background: '#fff',
                cursor: 'pointer', flex: 1, minWidth: '160px'
              }}
            >
              <option value="">All Houses</option>
              {HOUSES.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>

            {/* Country filter */}
            <select
              value={countryFilter}
              onChange={e => setCountryFilter(e.target.value)}
              style={{
                padding: '8px 12px', border: '2px solid #e0e0e0',
                borderRadius: '8px', fontSize: '14px',
                outline: 'none', background: '#fff',
                cursor: 'pointer', flex: 1, minWidth: '160px'
              }}
            >
              <option value="">All Countries</option>
              {countries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Clear filters */}
            {(search || houseFilter || countryFilter) && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px', background: '#fff',
                  cursor: 'pointer', fontSize: '13px',
                  color: '#666', fontWeight: '600'
                }}
              >
                ✕ Clear
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: '#c0392b', fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{
            textAlign: 'center', padding: '60px',
            color: '#666', fontSize: '14px'
          }}>
            Loading members...
          </div>
        )}

        {/* No results */}
        {!loading && filtered.length === 0 && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '60px', textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              🔍
            </div>
            <h3 style={{ color: NAVY, margin: '0 0 8px' }}>
              No members found
            </h3>
            <p style={{ color: '#666', margin: '0 0 16px' }}>
              Try adjusting your search or filters.
            </p>
            <button
              onClick={clearFilters}
              style={{
                background: NAVY, color: '#fff',
                border: 'none', borderRadius: '8px',
                padding: '10px 24px', cursor: 'pointer',
                fontSize: '14px', fontWeight: 'bold'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Member Grid */}
        {!loading && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {filtered.map(member => {
              const badge = getRoleColor(member.role);
              return (
                <div
                  key={member.id}
                  onClick={() => navigate(`/profile/${member.id}`)}
                  style={{
                    background: '#fff', borderRadius: '12px',
                    padding: '20px', cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    transition: 'transform 0.1s, box-shadow 0.1s',
                    border: '2px solid transparent'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 6px 16px rgba(0,0,0,0.12)';
                    e.currentTarget.style.borderColor = GOLD;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0,0,0,0.06)';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  {/* Card Header */}
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: '14px', marginBottom: '14px'
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width: '52px', height: '52px',
                      borderRadius: '50%', background: NAVY,
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '20px',
                      fontWeight: 'bold', color: GOLD,
                      flexShrink: 0
                    }}>
                      {getInitials(member.name)}
                    </div>

                    {/* Name block */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 'bold', fontSize: '15px',
                        color: '#333', marginBottom: '2px',
                        whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {member.name}
                      </div>
                      {member.nickname && (
                        <div style={{
                          fontSize: '13px', color: GOLD,
                          fontStyle: 'italic'
                        }}>
                          "{member.nickname}"
                        </div>
                      )}
                      {member.maritalName && !member.nickname && (
                        <div style={{
                          fontSize: '12px', color: '#888'
                        }}>
                          née {member.maritalName}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Details */}
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    gap: '6px'
                  }}>
                    {/* House */}
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: '6px', fontSize: '13px', color: '#555'
                    }}>
                      <span>🏠</span>
                      <span>{member.boardingHouse} House</span>
                    </div>

                    {/* Occupation */}
                    {member.occupation && (
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        gap: '6px', fontSize: '13px', color: '#555'
                      }}>
                        <span>💼</span>
                        <span style={{
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {member.occupation}
                        </span>
                      </div>
                    )}

                    {/* Location */}
                    {member.location && (
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        gap: '6px', fontSize: '13px', color: '#555'
                      }}>
                        <span>📍</span>
                        <span style={{
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {member.location}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div style={{
                    marginTop: '14px', paddingTop: '12px',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      background: badge.bg, color: badge.color,
                      padding: '3px 8px', borderRadius: '10px',
                      fontSize: '11px', fontWeight: 'bold'
                    }}>
                      {member.role === 'house_rep'  ? 'House Rep'  :
                       member.role === 'executive'  ? 'Executive'  :
                       member.role === 'treasurer'  ? 'Treasurer'  :
                       'Member'}
                    </span>
                    <span style={{
                      fontSize: '12px', color: '#aaa'
                    }}>
                      View profile →
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats footer */}
        {!loading && members.length > 0 && (
          <div style={{
            marginTop: '24px', padding: '16px 20px',
            background: '#fff', borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            display: 'flex', gap: '24px',
            flexWrap: 'wrap', justifyContent: 'center'
          }}>
            {[
              { label: 'Total Members', value: members.length },
              { label: 'Showing',       value: filtered.length },
              { label: 'Houses',        value: 14 },
              { label: 'Countries',     value: countries.length },
            ].map(stat => (
              <div key={stat.label} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '24px', fontWeight: 'bold', color: NAVY
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}